import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import {
  TransactionDetailRenderer,
  TransactionDetailLoading,
  TransactionDetailError,
} from './TransactionDetailRenderer';
import type { TransactionDetailDto } from './transactionDetailTypes';
import { generateTransactionReceipt } from './receiptGenerator';

// ── props ─────────────────────────────────────────────────────────────────────

interface CanonicalTransactionDetailsDialogProps {
  open: boolean;
  onClose: () => void;

  /**
   * Provide EITHER a pre-fetched detail object OR a transactionId (+ optional fetcher).
   * When transactionId is provided, the dialog fetches from `detailEndpoint`.
   */
  detail?: TransactionDetailDto | null;
  transactionId?: string;

  /**
   * Async function that fetches the TransactionDetailDto for `transactionId`.
   * Typically wraps an axios/fetch call to your portal's API endpoint.
   */
  fetchDetail?: (transactionId: string) => Promise<TransactionDetailDto>;

  /** Passed to the renderer to show audit-level fee inputs (admin only) */
  showAuditFields?: boolean;

  /** Called when "View Message" is clicked (mailbox context) */
  onViewMessage?: (mailId: string) => void;

  /** Dialog title override. Defaults to "Transaction Details". */
  title?: string;

  /** Optional controls rendered in the title bar (e.g., admin toggles). */
  titleControls?: React.ReactNode;
}

// ── component ─────────────────────────────────────────────────────────────────

export const CanonicalTransactionDetailsDialog: React.FC<CanonicalTransactionDetailsDialogProps> = ({
  open,
  onClose,
  detail: detailProp,
  transactionId,
  fetchDetail,
  showAuditFields = false,
  onViewMessage,
  title = 'Transaction Details',
  titleControls,
}) => {
  const [detail, setDetail] = useState<TransactionDetailDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    if (detailProp) {
      setDetail(detailProp);
      setLoading(false);
      setError(null);
      return;
    }

    if (transactionId && fetchDetail) {
      setLoading(true);
      setError(null);
      fetchDetail(transactionId)
        .then((d) => {
          setDetail(d);
          setLoading(false);
        })
        .catch((err) => {
          setError(err?.message || 'Failed to load transaction details');
          setLoading(false);
        });
    }
  }, [open, detailProp, transactionId, fetchDetail]);

  const handleClose = () => {
    setDetail(null);
    setError(null);
    onClose();
  };

  const handleDownloadReceipt = () => {
    if (!detail) return;
    const receipt = generateTransactionReceipt(detail);
    const blob = new Blob([receipt.text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = receipt.filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const mailId = detail?.identity?.linked?.mailId;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>{title}</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          {titleControls}
          <IconButton onClick={handleClose} size="small" aria-label="close">
            <CloseIcon />
          </IconButton>
        </span>
      </DialogTitle>

      <DialogContent dividers sx={{ minHeight: 200 }}>
        {loading && <TransactionDetailLoading />}
        {error && <TransactionDetailError message={error} />}
        {!loading && !error && detail && (
          <TransactionDetailRenderer detail={detail} showAuditFields={showAuditFields} />
        )}
      </DialogContent>

      <DialogActions>
        {onViewMessage && mailId && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => { onViewMessage(mailId); handleClose(); }}
          >
            View Message
          </Button>
        )}
        {detail && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadReceipt}
          >
            Save Receipt
          </Button>
        )}
        <Button onClick={handleClose} variant="contained" size="small">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CanonicalTransactionDetailsDialog;
